<template>
  <div
    ref="container"
    class="LocationSelector relative select-none"
    :class="[textClass, iconOnly ? 'flex items-center' : 'inline-flex items-center h-full type-xs']"
  >
    <button
      type="button"
      class="flex items-center gap-4"
      :class="iconOnly ? '' : ['underline', { capitalize: locationStore.location?.label }]"
      :aria-expanded="expanded"
      :aria-label="iconOnly ? (locationStore.location ? label : $t('header.location.enter')) : undefined"
      @click="toggle"
    >
      <GlobalIcon
        name="location"
        :size="iconOnly ? 'medium' : 'xsmall'"
        :class="iconOnly ? '!w-24 !h-24' : ''"
      />
      <template v-if="!iconOnly">
        {{ locationStore.location ? label : $t('header.location.enter') }}
      </template>
    </button>

    <div
      v-if="expanded"
      class="bg-lightest text-text-base outline outline-1 outline-bg-inverse/10 p-16 flex flex-col gap-16 whitespace-normal text-left"
      :class="iconOnly ? 'fixed inset-x-16 top-64 shadow-lg z-modals' : 'absolute top-full mt-8 right-0 shadow-lg z-modals w-320'"
    >
      <div class="flex items-start justify-between gap-16">
        <div class="font-lora text-18 leading-24">{{ $t('header.location.title') }}</div>
        <button
          type="button"
          class="shrink-0 mt-2"
          :aria-label="$t('wcag.arialabel.close')"
          @click="expanded = false"
        >
          <GlobalIcon
            name="close"
            size="xsmall"
            class="!w-16 !h-16"
          />
        </button>
      </div>

      <p class="type-sm text-text-subtle">{{ $t('header.location.text') }}</p>

      <div class="flex flex-col gap-8">
        <label for="headerLocationSearch" class="type-headline-xs">{{ $t('pdp.experience.storePanel.label') }}</label>
        <input
          id="headerLocationSearch"
          v-model="searchTerm"
          type="text"
          :placeholder="$t('pdp.experience.storePanel.placeholder')"
          class="w-full h-40 px-12 py-8 bg-bg-base border border-border-base type-sm"
          @keydown.enter="search"
        >
        <div v-if="errorKey" class="type-sm text-text-error">{{ $t(errorKey) }}</div>
      </div>

      <button
        type="button"
        class="btn w-full"
        :class="{ loading: searching }"
        @click="search"
      >
        {{ $t('modal.generic.save') }}
      </button>

      <button
        type="button"
        class="flex items-center gap-8 w-fit type-sm underline"
        :disabled="locating"
        @click="useMyPosition"
      >
        <GlobalIcon
          name="cursor-target"
          size="small"
          color="base"
        />
        {{ locating ? $t('pdp.experience.storePanel.locating') : $t('pdp.experience.storePanel.useLocation') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import useLocationSearch from '~/composables/useLocationSearch';
import { useLocationStore } from '~/store/location';

const props = defineProps({
  invertTextColor: {
    type: Boolean,
    default: null,
  },
  iconOnly: {
    type: Boolean,
    default: false,
  },
});

const { $t } = useNuxtApp();
const locationStore = useLocationStore();
const { searchTerm, searching, locating, errorKey, searchByTerm, useMyPosition } = useLocationSearch();

const expanded = ref(false);
const container = useTemplateRef('container');

onClickOutside(container, () => {
  expanded.value = false;
});

onMounted(() => locationStore.load());

const textClass = computed(() => {
  if (props.invertTextColor === null) {
    return '';
  }
  return props.invertTextColor ? 'text-text-inverse' : 'text-text-base';
});

const label = computed(() => locationStore.location?.label || $t('pdp.experience.storePanel.currentPosition'));

const toggle = () => {
  expanded.value = !expanded.value;
  if (expanded.value) {
    searchTerm.value = locationStore.location?.label ?? '';
    errorKey.value = '';
  }
};

const search = async() => {
  if (await searchByTerm()) {
    expanded.value = false;
  }
};

// a position from the browser also closes the box
watch(() => locationStore.location, () => {
  expanded.value = false;
});
</script>
